import { Link } from "react-router-dom";
import { MapPin, Phone, ShieldCheck, ArrowUpRight } from "lucide-react";
import { BRAND, CONTACT, whatsappUrl } from "@/lib/site";
import { scrollToTop } from "@/components/SmoothScroll";
import { useLang } from "@/context/LanguageContext";

export const Footer = () => {
  const { t } = useLang();
  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/products", label: t("nav_products") },
    { to: "/about", label: t("nav_about") },
    { to: "/contact", label: t("nav_contact") },
  ];

  return (
    <footer className="relative z-10 border-t border-gold/15 bg-navy-950" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 md:px-10 pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1.1fr]">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img src="/assets/logo.png" alt="Mulywan" className="h-14 w-14 rounded-full ring-1 ring-gold/40" />
              <span className="font-serif text-4xl text-gold-gradient">Mulywan</span>
            </div>
            <p className="max-w-md text-white/60 leading-relaxed font-light">{t("footer_blurb")}</p>
            <p className="mt-6 label-eyebrow">{t("brand_by")} {BRAND.company}</p>
          </div>

          <div className="grid grid-cols-2 gap-8 md:gap-12">
            <div>
              <h4 className="text-sm uppercase tracking-[0.25em] text-white/50 mb-6">{t("footer_explore")}</h4>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      onClick={scrollToTop}
                      data-testid={`footer-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                      className="group inline-flex items-center gap-1 text-white/70 hover:text-gold transition-colors font-light"
                    >
                      {l.label}
                      <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-[0.25em] text-white/50 mb-6">{t("footer_reach")}</h4>
              <ul className="space-y-4 text-white/70 font-light">
                <li className="flex gap-3">
                  <MapPin size={18} className="text-gold shrink-0 mt-0.5" />
                  <span>{BRAND.address}</span>
                </li>
                <li className="flex gap-3">
                  <Phone size={18} className="text-gold shrink-0 mt-0.5" />
                  <span className="flex flex-col">
                    <a href={whatsappUrl(CONTACT.primaryIntl, "Hello Mulywan!")} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors">+91 {CONTACT.primary}</a>
                    <a href={whatsappUrl(CONTACT.secondaryIntl, "Hello Mulywan!")} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors">+91 {CONTACT.secondary}</a>
                  </span>
                </li>
                <li className="flex gap-3">
                  <ShieldCheck size={18} className="text-gold shrink-0 mt-0.5" />
                  <span className="text-sm">FSSAI: {BRAND.fssai}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 md:items-center justify-between text-xs text-white/45">
          <p>© {new Date().getFullYear()} {BRAND.company}. {t("footer_rights")}</p>
          <p className="flex flex-wrap gap-x-6 gap-y-1">
            <span>GST: {BRAND.gst}</span>
            <span>UDYAM: {BRAND.udyam}</span>
          </p>
        </div>
      </div>
    </footer>
  );
};
